import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, Check, Calendar, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { LanguagePicker } from "../components/LanguagePicker";

export default function PublicRsvp() {
  const { slug, guestId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ rsvp_status: "attending", plus_one_name: "", meal_preference: "", dietary: "", note: "" });
  const [events, setEvents] = useState([]);

  useEffect(() => {
    localStorage.setItem(`lif_guest_${slug}`, guestId);
    api.get(`/public/rsvp/${slug}/${guestId}`).then((r) => {
      setData(r.data);
      setForm((f) => ({ ...f, rsvp_status: r.data.guest.rsvp_status === "pending" ? "attending" : r.data.guest.rsvp_status }));
    }).catch(() => setErr("RSVP link not found."));
    api.get(`/public/site/${slug}/events/${guestId}`).then((r) => setEvents(r.data || [])).catch(() => {});
  }, [slug, guestId]);

  async function rsvpEvent(eventId, rsvp_status) {
    try {
      await api.post(`/public/site/${slug}/events/${guestId}/rsvp`, {
        event_id: eventId, rsvp_status, plus_one: false,
      });
      setEvents((evs) => evs.map((e) => e.event_id === eventId ? { ...e, rsvp_status } : e));
      toast.success("Saved");
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/public/rsvp/${slug}/${guestId}`, form);
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  if (err) return <div className="min-h-screen flex items-center justify-center"><p>{err}</p></div>;
  if (!data) return null;

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-warm)] px-6">
        <LanguagePicker className="fixed top-3 right-3" />
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--gold)] flex items-center justify-center"><Check className="w-8 h-8 text-white" /></div>
          <p className="font-script text-[var(--gold)] text-4xl mb-2">Thank you</p>
          <h1 className="font-serif font-light text-4xl mb-4">{data.wedding.couple_names}</h1>
          <p className="text-[var(--muted)]">Your response has been recorded. We can't wait to celebrate with you.</p>
          <GuestPortalCta slug={slug} guestId={guestId} name={data.guest?.name} />
        </div>
      </div>
    );
  }

  const w = data.wedding;
  return (
    <div className="min-h-screen bg-[var(--bg)] py-12 px-6">
      <LanguagePicker className="fixed top-3 right-3" />
      <div className="max-w-md mx-auto">
        <div className="mb-6"><GuestPortalCta slug={slug} guestId={guestId} name={data.guest?.name} /></div>
        <div className="text-center mb-10">
          <Heart className="w-6 h-6 text-[var(--gold)] mx-auto mb-3" />
          <p className="font-script text-[var(--gold)] text-3xl">{w.couple_names}</p>
          {w.wedding_date && <p className="text-sm uppercase tracking-[0.22em] text-[var(--muted)] mt-2">{new Date(w.wedding_date).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</p>}
          <h1 className="font-serif font-light text-3xl mt-6">Hello, {data.guest.name}</h1>
          <p className="text-[var(--muted)] text-sm">Will you be joining us?</p>
        </div>

        <form onSubmit={submit} className="bg-white border border-[var(--line)] rounded-2xl p-6 space-y-5">
          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)] block mb-2">Your response</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: "attending", l: "Joyfully Yes" },
                { v: "maybe", l: "Maybe" },
                { v: "not_attending", l: "Decline" },
              ].map((o) => (
                <button key={o.v} type="button" data-testid={`rsvp-${o.v}`} onClick={() => setForm({ ...form, rsvp_status: o.v })} className={`py-3 rounded-full text-xs uppercase tracking-wider ${form.rsvp_status === o.v ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] text-[var(--muted)]"}`}>{o.l}</button>
              ))}
            </div>
          </div>

          {form.rsvp_status === "attending" && (
            <>
              {data.guest.plus_one_allowed && (
                <div>
                  <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Plus-one name (optional)</label>
                  <input value={form.plus_one_name} onChange={(e) => setForm({ ...form, plus_one_name: e.target.value })} className="input-luxe" />
                </div>
              )}
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Meal preference</label>
                <select data-testid="rsvp-meal" value={form.meal_preference} onChange={(e) => setForm({ ...form, meal_preference: e.target.value })} className="input-luxe">
                  <option value="">Select…</option>
                  {(data.wedding.meal_options?.length ? data.wedding.meal_options : ["Beef", "Chicken", "Fish", "Vegetarian", "Vegan", "Kids meal"]).map((o) => <option key={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Dietary needs or allergies</label>
                <input value={form.dietary} onChange={(e) => setForm({ ...form, dietary: e.target.value })} maxLength={160} placeholder="e.g. nut allergy, gluten free" className="input-luxe" data-testid="rsvp-dietary" />
              </div>
            </>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">A note for the couple</label>
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} className="input-luxe resize-none" />
          </div>

          <button data-testid="public-rsvp-submit" type="submit" disabled={busy} className="btn-gold w-full">{busy ? "Sending…" : "Send Response"}</button>
        </form>

        {/* Per-event RSVPs */}
        {events.length > 0 && (
          <div className="bg-white border border-[var(--line)] rounded-2xl p-6 mt-6" data-testid="event-rsvps">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-3">Events you're invited to</p>
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.event_id} className="border border-[var(--line)] rounded-xl p-4" data-testid={`event-${e.event_id}`}>
                  <p className="font-serif text-lg">{e.name}</p>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--muted)] mt-1 mb-3">
                    {e.event_datetime && <span><Calendar className="w-3 h-3 inline mr-1" />{new Date(e.event_datetime).toLocaleString()}</span>}
                    {e.venue && <span><MapPin className="w-3 h-3 inline mr-1" />{e.venue}</span>}
                  </div>
                  <div className="flex gap-2">
                    {[
                      { v: "attending", l: "Yes" },
                      { v: "not_attending", l: "No" },
                    ].map((o) => (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => rsvpEvent(e.event_id, o.v)}
                        data-testid={`event-rsvp-${e.event_id}-${o.v}`}
                        className={`py-2 px-4 rounded-full text-xs uppercase tracking-wider ${e.rsvp_status === o.v ? "bg-[var(--ink)] text-white" : "border border-[var(--line)] text-[var(--muted)]"}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function GuestPortalCta({ slug, guestId, name }) {
  const q = `slug=${encodeURIComponent(slug)}&g=${encodeURIComponent(guestId)}${name ? `&name=${encodeURIComponent(name)}` : ""}`;
  return (
    <div className="rounded-2xl border border-[var(--gold)]/40 bg-white p-4 text-left mt-6" data-testid="guest-portal-cta">
      <p className="font-serif text-xl">Your guest portal</p>
      <p className="text-xs text-[var(--muted)] mb-3">See your seat, the schedule, your photos and chat privately with the couple.</p>
      <div className="flex gap-2">
        <Link to={`/guest/signup?${q}`} className="btn-gold text-xs" data-testid="guest-portal-signup">Create login</Link>
        <Link to={`/guest/login?${q}`} className="btn-outline-gold text-xs" data-testid="guest-portal-login">Log in</Link>
      </div>
    </div>
  );
}
